import {useReducer} from 'react';

import {isNil} from 'ramda';

import {FinancingCompositionLayer, FinancingCompositionScene} from '@dms/api/shared';

import {randomLowerCaseUUID} from 'shared/web';

import {
  CompositionEditorAction,
  LayerAlignment,
  LayerBox,
  LayerReorder,
} from '../types/CompositionEditorAction';
import {CompositionEditorMode} from '../types/CompositionEditorMode';
import {
  canAddLayers,
  compositionEditorReducer,
  createCompositionEditorState,
} from '../utils/compositionEditorReducer';
import {createDefaultScene} from '../utils/createDefaultScene';
import {hasPhotoLayer} from '../utils/hasPhotoLayer';

export function useCompositionEditorState(
  initialScene: FinancingCompositionScene | null,
  mode: CompositionEditorMode
) {
  const [state, dispatch] = useReducer(
    compositionEditorReducer,
    initialScene ?? createDefaultScene(mode),
    createCompositionEditorState
  );

  const selectedLayer = isNil(state.selectedLayerId)
    ? null
    : (state.scene.layers.find((layer) => layer.id === state.selectedLayerId) ?? null);

  const send = (action: CompositionEditorAction) => dispatch(action);

  return {
    scene: state.scene,
    selectedLayer,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    canAddLayers: (count: number) => canAddLayers(state.scene, count),
    hasPhotoLayer: hasPhotoLayer(state.scene.layers),
    actions: {
      addLayers: (layers: FinancingCompositionLayer[]) => send({type: 'addLayers', layers}),
      updateLayer: (layerId: string, patch: Partial<FinancingCompositionLayer>) =>
        send({type: 'updateLayer', layerId, patch}),
      moveLayer: (layerId: string, dx: number, dy: number) =>
        send({type: 'moveLayer', layerId, dx, dy}),
      resizeLayer: (layerId: string, box: LayerBox) => send({type: 'resizeLayer', layerId, box}),
      removeLayer: (layerId: string) => send({type: 'removeLayer', layerId}),
      clearDeletedFont: (fontId: string) => send({type: 'clearDeletedFont', fontId}),
      duplicateLayer: (layerId: string) =>
        send({type: 'duplicateLayer', layerId, newLayerId: randomLowerCaseUUID()}),
      reorderLayer: (layerId: string, reorder: LayerReorder) =>
        send({type: 'reorderLayer', layerId, reorder}),
      alignLayer: (layerId: string, alignment: LayerAlignment) =>
        send({type: 'alignLayer', layerId, alignment}),
      setCanvasSize: (width: number, height: number) =>
        send({type: 'setCanvasSize', width, height}),
      setBackground: (background: FinancingCompositionScene['canvas']['background']) =>
        send({type: 'setBackground', background}),
      replaceScene: (scene: FinancingCompositionScene) => send({type: 'replaceScene', scene}),
      selectLayer: (layerId: string | null) => send({type: 'selectLayer', layerId}),
      undo: () => send({type: 'undo'}),
      redo: () => send({type: 'redo'}),
    },
  };
}
